import { IssueStateType } from './issueStateType';

/**
 * Native issue workflow — the single, fixed state list shared by every client.
 * Values are the display names, stored as-is in the Postgres enum
 * `issue_state` (same TS↔PG alignment rule as AllocationMode).
 */
export enum IssueState {
  Backlog = 'Backlog',
  Todo = 'Todo',
  InProgress = 'In Progress',
  InReview = 'In Review',
  Development = 'Development',
  Tested = 'Tested',
  Production = 'Production',
  Canceled = 'Canceled',
}

export interface IssueStateMeta {
  /** State category, same taxonomy the Kanban already consumes (state.type) */
  type: IssueStateType;
  /** Column dot color (hex) */
  color: string;
  /** Board order, left → right (Canceled is never a column) */
  position: number;
}

/**
 * Presentation + semantics of each state. This is what the API serializes as
 * `workflowStates` for the Kanban: { id: state, name: state, type, color,
 * position } — the front keeps receiving the exact shape it gets today.
 */
export const ISSUE_STATE_META: Record<IssueState, IssueStateMeta> = {
  [IssueState.Backlog]: { type: IssueStateType.Backlog, color: '#bec2c8', position: 0 },
  [IssueState.Todo]: { type: IssueStateType.Unstarted, color: '#e2e2e2', position: 1 },
  [IssueState.InProgress]: { type: IssueStateType.Started, color: '#f2c94c', position: 2 },
  [IssueState.InReview]: { type: IssueStateType.Started, color: '#5e6ad2', position: 3 },
  [IssueState.Development]: { type: IssueStateType.Completed, color: '#26b5ce', position: 4 },
  [IssueState.Tested]: { type: IssueStateType.Completed, color: '#4cb782', position: 5 },
  [IssueState.Production]: { type: IssueStateType.Completed, color: '#2da44e', position: 6 },
  [IssueState.Canceled]: { type: IssueStateType.Canceled, color: '#95a2b3', position: 7 },
};

/** All states, board order */
export const ISSUE_STATES: IssueState[] = Object.values(IssueState)
  .sort((a, b) => ISSUE_STATE_META[a].position - ISSUE_STATE_META[b].position);

/** States rendered as Kanban columns (Canceled excluded) */
export const BOARD_ISSUE_STATES: IssueState[] = ISSUE_STATES
  .filter((state) => state !== IssueState.Canceled);

/** States counting as "closed" for retention windows (completed or canceled) */
export function isClosedIssueState(state: IssueState): boolean {
  const { type } = ISSUE_STATE_META[state];
  return type === IssueStateType.Completed || type === IssueStateType.Canceled;
}

/**
 * Completed-type states (Development/Tested/Production) — a sub-issue counts
 * as "done" in its parent's progress badge from Development on (arbitrated
 * 2026-07-15); Canceled children leave the count entirely.
 */
export const COMPLETED_ISSUE_STATES: IssueState[] = ISSUE_STATES
  .filter((state) => ISSUE_STATE_META[state].type === IssueStateType.Completed);

/**
 * Kind of an issue timeline event (issueEvents.kind). Mirrors the history
 * kinds the front already renders, plus native-only kinds (validation,
 * visibility) that the DTO layer may translate for client display.
 */
export enum IssueEventKind {
  Created = 'created',
  State = 'state',
  Assignee = 'assignee',
  Priority = 'priority',
  Project = 'project',
  Title = 'title',
  Labels = 'labels',
  Validation = 'validation',
  Visibility = 'visibility',
  /** Sub-issue hierarchy: attached to / detached from a parent issue */
  Parent = 'parent',
  Other = 'other',
}
